import moment from "moment";
import {groupBy} from "lodash";
import { DateTime } from "luxon";
import FinancialAccount from "../FinancialAccount";
import {BalanceSheetReportCategory, BalanceSheetReportCategoryGroup} from "./BalanceSheetReport";
import GeneralHelper from "../../utils/GeneralHelper";

export default class BalanceSheet {
  /**
   * @param {string} date
   * @param {string} companyId
   * @param {array<FinancialAccount>} assets
   * @param {array<FinancialAccount>} liabilities
   * @param {array<FinancialAccount>} equity
   * @param {string} earliestAvailableMonth
   * @param {string} mostRecentAvailableMonth
   * @param {float} totalAssetsAmount
   * @param {float} totalLiabilitiesAmount
   * @param {float} totalEquityAmount
   * @param {float} netAssetsAmount
   */
  constructor({
    date,
    companyId,
    assets = [],
    liabilities = [],
    equity = [],
    earliestAvailableMonth,
    mostRecentAvailableMonth,
    netAssetsAmount
  }) {
    this.date = date
    this.companyId = companyId
    this.assets = assets
    this.liabilities = liabilities
    this.equity = equity
    this.earliestAvailableMonth = earliestAvailableMonth
    this.mostRecentAvailableMonth = mostRecentAvailableMonth
    this.netAssetsAmount = netAssetsAmount
  }

  get dateFormatted() {
    return DateTime.fromISO(this.date).toFormat("LL/dd/yyyy")
  }

  get totalExcess() {
    return 0
  }

  get formattedEarliestAvailableMonth() {
    return moment(this.earliestAvailableMonth).format("MMMM YYYY")
  }

  get formattedMostRecentAvailableMonth() {
    return moment(this.mostRecentAvailableMonth).format("MMMM YYYY")
  }

  get totalFixedAssets() {
    const fixedAssets = this.assets.filter((asset) => asset.name === "Fixed Assets")
    return GeneralHelper.sumObjectAttributeFromArray(fixedAssets, "value")
  }

  get totalCurrentAssets() {
    const currentAssets = this.assets.filter((asset) => asset.name === "Current Assets")
    return GeneralHelper.sumObjectAttributeFromArray(currentAssets, "value")
  }

  get totalCurrentLiabilities() {
    const currentLiabilities = this.liabilities.filter((liability) => liability.name === "Current Liabilities")
    return GeneralHelper.sumObjectAttributeFromArray(currentLiabilities, "value")
  }

  get totalLongTermLiability() {
    const longTermLiability = this.liabilities.filter((liability) => liability.name === "Long Term Liability")
    return GeneralHelper.sumObjectAttributeFromArray(longTermLiability, "value")
  }

  get totalAssetsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.assets, "value")
  }

  get totalEquityAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.equity, "value")
  }

  get totalLiabilitiesAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.liabilities, "value")
  }

  /**
   * This getter is responsible for generating the balance sheet report
   */
  get balanceSheetReport() {
    const { currentAssets, longTermAssets, currentLiabilities, longTermLiabilities } = FinancialAccount.getAllowedBalanceSheetItemGroups()

    const currentAssetsCategories = Object.keys(FinancialAccount.getAllowedAssetsCategories({ group: currentAssets }))
    const longTermAssetsCategories = Object.keys(FinancialAccount.getAllowedAssetsCategories({ group: longTermAssets }))
    const currentLiabilitiesCategories = Object.keys(FinancialAccount.getAllowedLiabilitiesCategories({ group: currentLiabilities }))
    const longTermLiabilitiesCategories = Object.keys(FinancialAccount.getAllowedLiabilitiesCategories({ group: longTermLiabilities }))

    const assetsItems = []
    this.assets.forEach((asset) => {
      asset.items.forEach(item => assetsItems.push(item))
    })

    const liabilitiesItems = []
    this.liabilities.forEach((liability) => {
      liability?.items.forEach(item => liabilitiesItems.push(item))
    })

    // Generate current asset report
    const currentAssetsItems = assetsItems.filter((asset) => currentAssetsCategories.includes(asset.category))
    const currentAssetsItemsGroupedByCategory = groupBy(currentAssetsItems, "category")

    const currentAssetsReport = Object.keys(currentAssetsItemsGroupedByCategory).map((category) => {
      const categoryData = FinancialAccount.getAllowedAssetsCategories()[category]

      return new BalanceSheetReportCategory({
        categoryName: categoryData?.name,
        categoryValue: categoryData?.value,
        balance: currentAssetsItemsGroupedByCategory[category]?.reduce((acc, obj) => {
          return acc + obj.value
        }, 0)
      })
    })

    // Generate long term icons report
    const longTermAssetsItems = assetsItems.filter((asset) => longTermAssetsCategories.includes(asset.category))
    const longTermAssetsGroupedByCategory = groupBy(longTermAssetsItems, "category")

    const longTermAssetsReport = Object.keys(longTermAssetsGroupedByCategory).map((category) => {
      const categoryData = FinancialAccount.getAllowedAssetsCategories()[category]
      return new BalanceSheetReportCategory({
        categoryName: categoryData?.name,
        categoryValue: categoryData?.value,
        balance: longTermAssetsGroupedByCategory[category]?.reduce((acc, obj) => {
          return acc + obj.value
        }, 0)
      })
    })

    // Generate current liabilities items
    const currentLiabilitiesItems = liabilitiesItems.filter(liability => currentLiabilitiesCategories.includes(liability?.category))
    const currentLiabilitiesGroupedByCategory = groupBy(currentLiabilitiesItems, "category")

    const currentLiabilitiesReport = Object.keys(currentLiabilitiesGroupedByCategory).map((category) => {
      const categoryData = FinancialAccount.getAllowedLiabilitiesCategories()[category]

      return new BalanceSheetReportCategory({
        categoryName: categoryData?.name,
        categoryValue: categoryData?.value,
        balance: currentLiabilitiesGroupedByCategory[category]?.reduce((acc, obj) => {
          return acc + obj.value
        }, 0)
      })
    })

    // Generate long term liabilities items
    const longTermLiabilitiesItems = liabilitiesItems.filter(liability => longTermLiabilitiesCategories.includes(liability?.category))
    const longTermLiabilitiesGroupedByCategory = groupBy(longTermLiabilitiesItems, "category")

    const longTermLiabilitiesReport = Object.keys(longTermLiabilitiesGroupedByCategory).map((category) => {
      const categoryData = FinancialAccount.getAllowedLiabilitiesCategories()[category]

      return new BalanceSheetReportCategory({
        categoryName: categoryData?.name,
        categoryValue: categoryData?.value,
        balance: longTermLiabilitiesGroupedByCategory[category]?.reduce((acc, obj) => {
          return acc + obj.value
        }, 0)
      })
    })

    return [
      new BalanceSheetReportCategoryGroup({
        groupName: currentAssets,
        categories: currentAssetsReport
      }),
      new BalanceSheetReportCategoryGroup({
        groupName: longTermAssets,
        categories: longTermAssetsReport
      }),
      new BalanceSheetReportCategoryGroup({
        groupName: currentLiabilities,
        categories: currentLiabilitiesReport
      }),
      new BalanceSheetReportCategoryGroup({
        groupName: longTermLiabilities,
        categories: longTermLiabilitiesReport
      })
    ]
  }
}